import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { addDocSafe } from '../firebase';
import './PrivacyRequests.css';
import Footer from '../unAuth/Components/footer';

function PrivacyRequests() {
  const [formData, setFormData] = useState({
    requestType: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    description: '',
    additionalInfo: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const requestTypes = [
    { value: 'access', label: 'Access my personal data' },
    { value: 'rectification', label: 'Correct inaccurate data' },
    { value: 'erasure', label: 'Delete my personal data' },
    { value: 'portability', label: 'Export my data' },
    { value: 'restriction', label: 'Restrict processing' },
    { value: 'objection', label: 'Object to processing' },
    { value: 'withdraw_consent', label: 'Withdraw consent' },
    { value: 'other', label: 'Other privacy concern' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.requestType) newErrors.requestType = 'Please select a request type';
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.description.trim()) newErrors.description = 'Please describe your request';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      const requestData = {
        ...formData,
        status: 'pending',
        requestId: `PR-${Date.now()}`,
        submittedAt: new Date().toISOString()
      };

      const result = await addDocSafe('privacy_requests', requestData);
      
      if (result.success) {
        setIsSubmitted(true);
        setFormData({
          requestType: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          description: '',
          additionalInfo: ''
        });
      } else {
        setErrors({ form: 'Failed to submit request. Please try again.' });
      }
    } catch (error) {
      console.error('Privacy request submission failed:', error);
      setErrors({ form: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="privacy-requests-page">
        <div className="container">
          <Link to="/" className="privacy-back">← Back to Home</Link>
          
          <div className="privacy-success">
            <h1>Privacy Request Submitted</h1>
            <div className="success-content">
              <div className="success-icon">✓</div>
              <h2>Thank you for your request</h2>
              <p>
                We have received your privacy request and will respond within 30 days 
                as required by applicable privacy laws.
              </p>
              <p>
                You will receive a confirmation email shortly with your request ID 
                and next steps.
              </p>
              <div className="success-actions">
                <Link to="/" className="success-btn">Return to Home</Link>
                <button 
                  onClick={() => setIsSubmitted(false)} 
                  className="success-btn secondary"
                >
                  Submit Another Request
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="privacy-requests-page">
      <div className="container">
        <Link to="/" className="privacy-back">← Back to Home</Link>
        
        <div className="privacy-requests-content">
          <h1>Privacy Requests</h1>
          <p className="privacy-intro">
            You have the right to access, correct, or delete your personal data. 
            Use this form to submit a Data Subject Access Request (DSAR) or other privacy-related requests.
          </p>

          <form className="privacy-requests-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="requestType">Request Type *</label>
              <select
                id="requestType"
                name="requestType"
                value={formData.requestType}
                onChange={handleInputChange}
                className={errors.requestType ? 'error' : ''}
              >
                <option value="">Select a request type</option>
                {requestTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.requestType && <span className="error-message">{errors.requestType}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={errors.firstName ? 'error' : ''}
                />
                {errors.firstName && <span className="error-message">{errors.firstName}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={errors.lastName ? 'error' : ''}
                />
                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number (Optional)</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description of Request *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                placeholder="Please provide details about your privacy request..."
                className={errors.description ? 'error' : ''}
              />
              {errors.description && <span className="error-message">{errors.description}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="additionalInfo">Additional Information (Optional)</label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleInputChange}
                rows="3"
                placeholder="Any additional information that might help us process your request..."
              />
            </div>

            {errors.form && <p className="error-message">{errors.form}</p>}

            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default PrivacyRequests;
